import { useMemo } from 'react';
import { usePosts, type PostMinimal } from '../posts';

/**
 * 自由検証 (free distribution) の対象となる「AI 解析済投稿」一覧を返す hook (Sprint Y)。
 *
 * 動作:
 *   - usePosts(uid, districtId) を内部で呼び出し、`aiAnalysisStatus === 'done'` のみを返す
 *   - 重複 subscribe (AppHome 等で既に usePosts 中) は許容 (Sprint V の `useMyVerification` 同等判断)
 *
 * 「free」は AssignmentSet.candidatePostIds や participantUid 制約を持たず、
 * user が任意の AI 解析済投稿を選んで検証する mode (verification.ts:160)。
 * docs/04 §4-3 (DistrictAiVerificationConfig.allowFreeVerification)、REQ-4.3.20〜25。
 *
 * 戻り値の `posts` は createdAt desc (usePosts 由来) が温存される。
 */

export type FreeVerificationCandidatesState =
  | { status: 'loading'; posts: []; error: null }
  | { status: 'ready'; posts: PostMinimal[]; error: null }
  | { status: 'error'; posts: []; error: Error };

/** AI 解析済 (status === 'done') の post のみ抽出 (pure、test 用 export)。 */
export function filterAiAnalyzedPosts(posts: PostMinimal[]): PostMinimal[] {
  return posts.filter((p) => p.aiAnalysisStatus === 'done');
}

export function useFreeVerificationCandidates(
  districtId: string,
  uid: string | null,
  /** R-70: 「自分」の uid 群 (端末引き継ぎ元を含む)。docs/28: 本人の写真の出し分けに使う。 */
  selfUids?: readonly string[],
): FreeVerificationCandidatesState {
  const postsState = usePosts(uid, districtId, 0, selfUids);

  const filtered = useMemo(() => {
    if (postsState.status !== 'ready') return [];
    return filterAiAnalyzedPosts(postsState.posts);
  }, [postsState]);

  if (postsState.status === 'loading') {
    return { status: 'loading', posts: [], error: null };
  }
  if (postsState.status === 'error') {
    return { status: 'error', posts: [], error: postsState.error };
  }
  return { status: 'ready', posts: filtered, error: null };
}
